import React, { useEffect, useMemo, useRef, useState } from "react";


const fmt = n => Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const fmtQ = n => Number(n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 3 });

const st = {
  kpis: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 14, marginBottom: 18 },
  kpi: { background: "#fff", border: "1.5px solid #e8e8f0", borderRadius: 14, padding: "16px 18px" },
  kpiLabel: { fontSize: "0.72rem", color: "#64748B", fontWeight: 700, textTransform: "uppercase" },
  kpiVal: { fontSize: "1.35rem", fontWeight: 800, color: "#0F172A", marginTop: 6 },
  bar: { display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 16 },
  input: { flex: 1, minWidth: 220, padding: "10px 14px", border: "1.5px solid #e8e8f0", borderRadius: 10, fontSize: "0.85rem" },
  select: { padding: "10px 12px", border: "1.5px solid #e8e8f0", borderRadius: 10, fontSize: "0.85rem", background: "#fff" },
  btn: { padding: "10px 14px", border: "1.5px solid #e8e8f0", borderRadius: 10, background: "#fff", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(290px,1fr))", gap: 14 },
  card: { background: "#fff", border: "1.5px solid #e8e8f0", borderRadius: 14, padding: 16, display: "flex", flexDirection: "column", gap: 10 },
  cell: { background: "#f8f7ff", borderRadius: 10, padding: "8px 10px" },
  cellLabel: { fontSize: "0.66rem", color: "#64748B", fontWeight: 700, textTransform: "uppercase" },
  cellVal: { fontSize: "0.92rem", fontWeight: 800, color: "#0F172A" },
  badge: { fontSize: "0.68rem", fontWeight: 700, padding: "3px 9px", borderRadius: 20 },
  overlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modal: { background: "#fff", borderRadius: 16, width: "min(900px,95vw)", maxHeight: "90vh", overflowY: "auto", padding: 24 },
};

export default function MesProduits({ apiUrl, authHeaders, societeId, exercice, getNomClient }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("ca");
  const [anomOnly, setAnomOnly] = useState(false);
  const [detail, setDetail] = useState(null);
  const [vue, setVue] = useState("ca");
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);



  const charger = async (refresh = false) => {
    if (!exercice) return;
    setLoading(true);
    setError("");
    try {
      const qs = new URLSearchParams({ exercice: String(exercice) });
      if (societeId) qs.set("societe_id", societeId);
      if (refresh) qs.set("refresh", "true");
      const r = await fetch(`${apiUrl}/produits?${qs}`, { headers: authHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `Erreur ${r.status}`);
      setData(d);
      clearTimeout(timer.current);
      if (d.en_cours) timer.current = setTimeout(() => charger(), 15000);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { charger(); }, [exercice, societeId]);

  const produits = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = (data?.produits || []).filter(p =>
      (!anomOnly || p.anomalies.length > 0) &&
      (!q || p.reference.toLowerCase().includes(q) || p.designation.toLowerCase().includes(q))
    );
    const tri = {
      ca: (a, b) => b.ca_ht - a.ca_ht,
      quantite: (a, b) => b.quantite - a.quantite,
      remise: (a, b) => (b.remise_mt || 0) - (a.remise_mt || 0),
      factures: (a, b) => b.nb_factures - a.nb_factures,
      nom: (a, b) => a.designation.localeCompare(b.designation),
    }[sort];
    return [...list].sort(tri);
  }, [data, search, sort, anomOnly]);

  const exporterCSV = () => {
    const entete = ["Reference", "Designation", "Unite", "Quantite", "CA HT", "Factures", "Clients", "Prix moyen", "Prix min", "Prix max", "TVA", "Anomalies"];
    const lignes = produits.map(p => [
      p.reference, p.designation, p.unite, p.quantite, p.ca_ht, p.nb_factures, p.nb_clients,
      p.prix_moyen, p.prix_min, p.prix_max, p.taux_tva.map(t => t + "%").join(" / "), p.anomalies.join(" | "),
    ].map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(";"));
    const blob = new Blob(["\uFEFF" + [entete.join(";"), ...lignes].join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `produits_${exercice}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (loading && !data) {
    return (
      <div style={{ textAlign: "center", padding: 50, color: "#64748B" }}>
        <div style={{ width: 34, height: 34, border: "3px solid #f0efff", borderTop: "3px solid #6c63ff", borderRadius: "50%", margin: "0 auto 14px" }} className="spin" />
        Analyse des lignes de factures {exercice}… (la première fois peut prendre jusqu'à 1 minute)
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 20, color: "#dc2626" }}>
        ❌ {error} <button style={{ ...st.btn, display: "inline-flex", marginLeft: 10 }} onClick={() => charger()}>Réessayer</button>
      </div>
    );
  }

  if (!data) return null;

  const top = data.produits[0];

  return (
    <div>
      <div style={st.kpis}>
        <div style={st.kpi}><div style={st.kpiLabel}>📦 Produits vendus</div><div style={st.kpiVal}>{data.nb_produits}</div><div style={{ fontSize: "0.72rem", color: "#64748B" }}>sur {data.nb_factures} factures</div></div>
        <div style={st.kpi}><div style={st.kpiLabel}>💰 CA HT</div><div style={st.kpiVal}>{fmt(data.ca_ht_total)}</div><div style={{ fontSize: "0.72rem", color: "#64748B" }}>TND — exercice {exercice}</div></div>
        <div style={st.kpi}><div style={st.kpiLabel}>💵 CA TTC</div><div style={{ ...st.kpiVal, color: "#16a34a" }}>{fmt(data.produits.reduce((s, p) => s + (p.ca_ht || 0) + (p.montant_tva || 0), 0) + (data.timbre_total || 0))}</div><div style={{ fontSize: "0.72rem", color: "#64748B" }}>TND — HT + TVA + Timbre ({data.timbre_total || 0})</div></div>
        <div style={st.kpi}><div style={st.kpiLabel}>🏷️ Remises accordées</div><div style={{ ...st.kpiVal, color: "#dc2626" }}>{fmt(data.remise_total)}</div><div style={{ fontSize: "0.72rem", color: "#64748B" }}>{(data.ca_ht_total + (data.remise_total || 0)) ? ((data.remise_total || 0) / (data.ca_ht_total + (data.remise_total || 0)) * 100).toFixed(2) : "0.00"}% du CA brut</div></div>
        <div style={st.kpi}><div style={st.kpiLabel}>🏆 Top produit</div><div style={{ ...st.kpiVal, fontSize: "0.95rem" }}>{top ? top.designation : "—"}</div><div style={{ fontSize: "0.72rem", color: "#64748B" }}>{top ? fmt(top.ca_ht) + " TND" : ""}</div></div>
        <div style={{ ...st.kpi, cursor: "pointer", borderColor: anomOnly ? "#f59e0b" : "#e8e8f0" }} onClick={() => setAnomOnly(v => !v)}><div style={st.kpiLabel}>⚠️ Anomalies</div><div style={{ ...st.kpiVal, color: data.nb_anomalies ? "#d97706" : "#16a34a" }}>{data.nb_anomalies}</div><div style={{ fontSize: "0.72rem", color: "#64748B" }}>cliquer pour filtrer</div></div>
      </div>

      <div style={st.bar}>
        <input style={st.input} placeholder="🔍 Rechercher par code ou désignation…" value={search} onChange={e => setSearch(e.target.value)} />
        <select style={st.select} value={sort} onChange={e => setSort(e.target.value)}>
          <option value="ca">Trier par Chiffre d'affaires</option>
          <option value="quantite">Trier par Quantité vendue</option>
          <option value="remise">Trier par Remise accordée</option>
          <option value="factures">Trier par Nombre de factures</option>
          <option value="nom">Trier par Nom</option>
        </select>
        <label style={st.btn}>
          <input type="checkbox" checked={anomOnly} onChange={e => setAnomOnly(e.target.checked)} />
          Anomalies uniquement
        </label>
        <button style={st.btn} onClick={exporterCSV}>📥 Export Excel</button>
        <button style={st.btn} onClick={() => charger(true)} disabled={loading}>{loading ? "⏳" : "🔄"} Actualiser</button>
      </div>

      <div style={{ fontSize: "0.8rem", color: "#64748B", marginBottom: 12 }}>
        <strong style={{ color: "#0F172A" }}>{produits.length}</strong> produit(s) sur {data.nb_produits}
        {data.maj_le && <span style={{ marginLeft: 10 }}>🕒 Mis à jour le {new Date(data.maj_le * 1000).toLocaleString("fr-FR")}</span>}
        {data.en_cours && <span style={{ color: "#6c63ff", marginLeft: 10, fontWeight: 600 }}>⏳ Mise à jour en cours… (rechargement automatique)</span>}

        {data.factures_en_erreur?.length > 0 && <span style={{ color: "#d97706", marginLeft: 10 }}>⚠️ {data.factures_en_erreur.length} facture(s) non lues</span>}
      </div>

      <div style={st.grid}>
        {produits.map(p => (
          <div key={p.reference || p.designation} style={{ ...st.card, borderColor: p.anomalies.length ? "#fcd34d" : "#e8e8f0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <div style={{ fontWeight: 800, color: "#0F172A", fontSize: "0.88rem" }}>📦 {p.designation}</div>
              {p.taux_tva.length > 0 && <span style={{ ...st.badge, color: "#6c63ff", background: "#f0efff", whiteSpace: "nowrap" }}>TVA {p.taux_tva.join("/")}%</span>}
            </div>
            <div style={{ fontSize: "0.74rem", color: "#64748B" }}>Code : {p.reference || "—"} · Unité : {p.unite || "—"} · Monnaie : <strong style={{ color: (p.devises || []).length > 1 ? "#d97706" : "#0F172A" }}>{(p.devises || []).join(" / ") || "—"}</strong></div>
<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
  <div style={st.cell}><div style={st.cellLabel}>Qté vendue</div><div style={st.cellVal}>{fmtQ(p.quantite)}</div></div>
  <div style={st.cell}><div style={st.cellLabel}>Prix unit. moy.</div><div style={st.cellVal}>{fmt(p.prix_moyen)}</div></div>
  <div style={{ ...st.cell, background: "#f0efff" }}><div style={st.cellLabel}>MT total HT</div><div style={{ ...st.cellVal, color: "#6c63ff" }}>{fmt(p.ca_ht)}</div></div>
</div>
<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
  <div style={st.cell}><div style={st.cellLabel}>Factures</div><div style={st.cellVal}>{p.nb_factures}</div></div>
  <div style={st.cell}><div style={st.cellLabel}>Clients</div><div style={st.cellVal}>{p.nb_clients}</div></div>
  <div style={{ ...st.cell, background: "#f0fdf4" }}><div style={st.cellLabel}>MT total TTC</div><div style={{ ...st.cellVal, color: "#16a34a" }}>{fmt((p.ca_ht || 0) + (p.montant_tva || 0))}</div></div>
</div>

            <div style={{ fontSize: "0.74rem", color: "#475569" }}>Prix moyen {fmt(p.prix_moyen)} (min {fmt(p.prix_min)} / max {fmt(p.prix_max)})</div>
                        {p.remise_mt > 0 && <div style={{ fontSize: "0.74rem", color: "#dc2626", fontWeight: 600 }}>🏷️ Remise {p.remise_pct}% · Manque à gagner {fmt(p.remise_mt)}</div>}
            {p.anomalies.map(a => <span key={a} style={{ ...st.badge, color: "#b45309", background: "#fef3c7", alignSelf: "flex-start" }}>⚠️ {a}</span>)}
            <button style={{ ...st.btn, justifyContent: "center", color: "#6c63ff" }} onClick={() => setDetail(p)}>Voir détail →</button>
          </div>
        ))}
      </div>

      {detail && (() => {
        const NOMS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];
        const VUES = { ca: "CA HT", qte: "Qté vendue", prix: "Prix moyen" };
        const COUL = { ca: "#6c63ff", qte: "#10b981", prix: "#f59e0b" };
        const caM = detail.ventes_par_mois || {};
        const qM = detail.qte_par_mois || {};
        const mois = NOMS.map((nom, i) => {
          const m = exercice + "-" + String(i + 1).padStart(2, "0");
          const v = vue === "qte" ? (qM[m] || 0) : vue === "prix" ? (qM[m] ? (caM[m] || 0) / qM[m] : 0) : (caM[m] || 0);
          return [m, v, nom];
        });
        const maxMois = Math.max(...mois.map(([, v]) => v), 0) || 1;
        const fv = v => (vue === "qte" ? fmtQ(v) : fmt(v));

        return (
          <div style={st.overlay} onClick={() => setDetail(null)}>
            <div style={st.modal} onClick={e => e.stopPropagation()}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontSize: "0.72rem", color: "#6c63ff", fontWeight: 700, textTransform: "uppercase" }}>Produit · {detail.reference || "sans code"}</div>
                  <h3 style={{ margin: "4px 0 0", color: "#0F172A" }}>{detail.designation}</h3>
                </div>
                <button style={{ ...st.btn, fontSize: "1.1rem" }} onClick={() => setDetail(null)}>×</button>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, margin: "22px 0 10px" }}>
                <h4 style={{ margin: 0, color: "#0F172A" }}>📈 Ventes par mois — {exercice} ({VUES[vue]})</h4>
                <div style={{ display: "flex", gap: 6 }}>
                  {Object.entries(VUES).map(([k, lbl]) => (
                    <button key={k} onClick={() => setVue(k)} style={{ ...st.btn, padding: "6px 12px", background: vue === k ? COUL[k] : "#fff", color: vue === k ? "#fff" : "#334155", borderColor: vue === k ? COUL[k] : "#e8e8f0" }}>{lbl}</button>
                  ))}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 170, borderBottom: "1.5px solid #e8e8f0", paddingBottom: 4 }}>
                {mois.map(([m, v, nom]) => (
                  <div key={m} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }} title={nom + " " + exercice + " : " + fv(v)}>
                    {v > 0 && <div style={{ fontSize: "0.62rem", fontWeight: 700, color: COUL[vue], whiteSpace: "nowrap" }}>{fv(v)}</div>}
                    <div style={{ width: "100%", maxWidth: 40, height: v ? Math.max(4, (v / maxMois) * 115) : 0, background: COUL[vue], borderRadius: "6px 6px 0 0" }} />
                    <div style={{ fontSize: "0.66rem", color: "#64748B" }}>{nom}</div>
                  </div>
                ))}
              </div>


              {(() => {
                const cl = detail.clients_detail || [];
                const totQ = cl.reduce((s, c) => s + c.quantite, 0);
                const totHT = cl.reduce((s, c) => s + c.ca_ht, 0);
                const totRem = cl.reduce((s, c) => s + (c.remise_mt || 0), 0);
                const th = { textAlign: "right", padding: "8px 10px", fontSize: "0.7rem", color: "#64748B", textTransform: "uppercase", background: "#f8f7ff", position: "sticky", top: 0 };
                const td = { textAlign: "right", padding: "8px 10px", borderBottom: "1px solid #f1f5f9" };
                const tf = { ...td, position: "sticky", bottom: 0, background: "#f0efff", fontWeight: 800, color: "#6c63ff" };
                return (
                  <>
                    <h4 style={{ margin: "22px 0 10px", color: "#0F172A" }}>👥 Clients ({cl.length})</h4>
                    {cl.length === 0 ? (
                      <div style={{ fontSize: "0.82rem", color: "#64748B" }}>Aucun détail client — cliquer sur 🔄 Actualiser</div>
                    ) : (
                      <div style={{ maxHeight: 320, overflowY: "auto", border: "1.5px solid #e8e8f0", borderRadius: 10 }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
                          <thead>
                            <tr>
                              <th style={{ ...th, textAlign: "left" }}>Client</th>
                              <th style={th}>Qté</th>
                              <th style={th}>Prix unit.</th>
                              <th style={th}>Remise</th>
                              <th style={th}>Montant total HT</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cl.map((c, i) => (
                              <tr key={c.code_client || i}>
                                <td style={{ ...td, textAlign: "left" }}>{i + 1}. {c.code_client ? (getNomClient ? getNomClient(c.code_client) : c.code_client) : "Sans client"}</td>
                                <td style={td}>{fmtQ(c.quantite)}</td>
                                <td style={td}>
                                  {fmt(c.prix_unitaire)}
                                  {c.prix_max > c.prix_min && <div style={{ fontSize: "0.66rem", color: "#94a3b8" }}>min {fmt(c.prix_min)} / max {fmt(c.prix_max)}</div>}
                                </td>
                                <td style={{ ...td, color: c.remise_mt > 0 ? "#dc2626" : "#94a3b8" }}>{c.remise_mt > 0 ? fmt(c.remise_mt) + " (" + c.remise_pct + "%)" : "—"}</td>
                                <td style={{ ...td, fontWeight: 700 }}>{fmt(c.ca_ht)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td style={{ ...tf, textAlign: "left" }}>TOTAL</td>
                              <td style={tf}>{fmtQ(totQ)}</td>
                              <td style={tf}>{fmt(totQ ? totHT / totQ : 0)}</td>
                              <td style={tf}>{fmt(totRem)}</td>
                              <td style={tf}>{fmt(totHT)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </>
                );
              })()}


              <h4 style={{ margin: "22px 0 10px", color: "#0F172A" }}>💲 Prix et TVA</h4>
              <div style={{ fontSize: "0.84rem", color: "#334155" }}>
                Moyen {fmt(detail.prix_moyen)} · Min {fmt(detail.prix_min)} · Max {fmt(detail.prix_max)} · TVA {detail.taux_tva.join(" / ") || "—"}% · TVA totale {fmt(detail.montant_tva)} TND
              </div>
              {detail.anomalies.length > 0 && (
                <div style={{ marginTop: 14, padding: 12, background: "#fef3c7", borderRadius: 10, color: "#92400e", fontSize: "0.82rem" }}>
                  {detail.anomalies.map(a => <div key={a}>⚠️ {a}</div>)}
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
